import {
  SKIP_EXERCISE_CREATE_INSTRUCTIONS_OFFICE,
  SKIP_EXERCISE_CREATE_INSTRUCTIONS_PATIENT,
} from '@src/const/localStorageConsts';
import {
  CreateExerciseDto,
  ExerciseFullSizeDto,
  ExerciseVideoDto,
  Visibility,
} from '@therame/shared';
import { inject, provide, reactive, type InjectionKey } from 'vue';

/**
 * The exercise-creation wizard's position and its data (TM-9455 / ADR-0029 §H-4).
 *
 * This replaces an XState machine that was a module-level actor started at import — one
 * wizard for the whole application, with its side effects inside state `entry` blocks and
 * its back-button availability pushed into `sidebarStore` from a subscriber. What is left
 * here is a **step table**: one row per step carrying its title, its forward edge and its
 * back edge, and pure functions that read it.
 *
 * Everything in this module takes the wizard as an argument and returns or mutates only
 * that argument, so the whole table can be tested against a constructed object with no
 * Pinia, no router and no component. The one exception is {@link skipsInstructions},
 * which reads the user's device preference out of `localStorage` — it is a guard input,
 * not wizard state, and it is called from exactly one place per edge.
 *
 * Side effects that used to live in state transitions do NOT live here: creating the
 * exercise, notifying the upload socket and the keycloak hand-off all belong to the
 * panel, beside every other panel's mutation.
 *
 * Neither does the wizard itself. There is no longer ONE of them: a wizard is a draft
 * buffer (`draftDefinitions.create`), so a page can hold several half-built Übungen at
 * once and none of them outlives a logout. `AddExerciseModal` takes its own from the
 * draft seam and hands it down with {@link provideAddExerciseWizard}; its step
 * components take it back with {@link useAddExerciseWizard}. That is the whole of what
 * used to be a module-level object every one of them imported.
 */

/** Where the user is in the wizard. */
export type AddExerciseStep =
  | 'chooseVisibility'
  | 'choosePatient'
  | 'showInstructions'
  | 'showUploadScreen'
  | 'showLoginScreen'
  | 'showMetadataEditor'
  | 'showPreview'
  | 'showFinishedScreen'
  | 'redirectToLibrary';

/**
 * Whether a video is on its way, and how it is arriving. Orthogonal to
 * {@link AddExerciseStep}: "the user is on the upload step" and "a file is uploading"
 * are independent facts, which is what the six nested sub-states were expressing and
 * what folding them into the step list would lose.
 */
export type AddExerciseUploadStatus =
  | 'noFile'
  | 'waitingForSmartphone'
  | 'waitingForSmartphoneUpload'
  | 'uploading'
  | 'uploadFailed'
  | 'uploadFinished';

export interface AddExerciseWizard {
  step: AddExerciseStep;
  uploadStatus: AddExerciseUploadStatus;
  /** The exercise being built. Replaced wholesale when the wizard returns to step one. */
  exercise: CreateExerciseDto;
  /** Set once the exercise exists — by this device's save, or by the phone's socket event. */
  createdExercise?: ExerciseFullSizeDto;
  /** The chosen Patient:in's display name, for the QR link and the preview copy. */
  sharedWithName?: string;
  /** Names the upload session the phone and the desktop share. */
  qrCodeToken?: string;
  /** A phone has answered the QR code, so removing the file returns to waiting for it. */
  connectedToSmartphone: boolean;
  /** This device IS the phone: it uploads, authenticates and redirects to the library. */
  isRemoteSmartphone: boolean;
}

/**
 * A fresh wizard, at step one with nothing entered — one Entwurf's worth of state, and
 * what `draftDefinitions` hands a panel that has no draft to restore.
 *
 * `qrCodeToken` and `isRemoteSmartphone` start empty like everything else, which is what
 * ended the machine's `RESET` hazard rather than a function of its own: nothing ever
 * unset `connectedToSmartphone`, so a reopened wizard still believed a phone was
 * attached and sent "Video entfernen" to "warten auf Videoupload..." with nothing on the
 * other end. A discarded Entwurf's buffer is gone, so the next one is genuinely new.
 */
export function createAddExerciseWizard(): AddExerciseWizard {
  return reactive<AddExerciseWizard>({
    step: 'chooseVisibility',
    uploadStatus: 'noFile',
    exercise: new CreateExerciseDto(),
    createdExercise: undefined,
    sharedWithName: undefined,
    qrCodeToken: undefined,
    connectedToSmartphone: false,
    isRemoteSmartphone: false,
  });
}

/**
 * The wizard the exercise-creation panel and its step components are driving.
 *
 * Provision rather than a store lookup, because there is no longer one wizard to look
 * up (TM-9455 / ADR-0029 §H-4): the panel takes its buffer from the draft seam, so two
 * parked Entwürfe are two wizards and a step component has to be handed the one it is
 * inside.
 */
const AddExerciseWizardKey: InjectionKey<AddExerciseWizard> = Symbol('addExerciseWizard');

/** Hand this panel's wizard to its step components. Called by `AddExerciseModal`. */
export function provideAddExerciseWizard(wizard: AddExerciseWizard): void {
  provide(AddExerciseWizardKey, wizard);
}

/**
 * The wizard this component is a step of.
 *
 * Throws rather than falling back to one of its own: a step component that silently got
 * a private wizard would render, accept input and lose every keystroke, which is exactly
 * the class of silent failure the seam exists to close.
 */
export function useAddExerciseWizard(): AddExerciseWizard {
  const wizard = inject(AddExerciseWizardKey, null);
  if (!wizard) {
    throw new Error(
      'No exercise wizard provided — this component must render inside AddExerciseModal.',
    );
  }
  return wizard;
}

/** Does this exercise belong to a single Patient:in rather than to a library? */
export function isForSinglePatient(exercise: CreateExerciseDto): boolean {
  return exercise.visibility === Visibility.ONE_PATIENT;
}

/**
 * Has the user ticked "don't show this again" for the instruction step this exercise
 * would reach? The two visibilities keep separate preferences, because the patient
 * instructions carry the consent wording and the office ones do not.
 *
 * The one reader of anything outside the wizard in this module.
 */
export function skipsInstructions(exercise: CreateExerciseDto): boolean {
  const key = isForSinglePatient(exercise)
    ? SKIP_EXERCISE_CREATE_INSTRUCTIONS_PATIENT
    : SKIP_EXERCISE_CREATE_INSTRUCTIONS_OFFICE;
  return localStorage.getItem(key) === 'true';
}

/**
 * What "Zurück" does from a step: an earlier step, or — on the upload step — a step back
 * inside the upload itself. `undefined` means there is nowhere to go, which is also what
 * hides the button.
 */
export type AddExerciseBackTarget = { step: AddExerciseStep } | 'undoUpload' | undefined;

/**
 * The upload statuses "Zurück" undoes in place. `noFile` has nothing to undo and
 * `uploading` has nothing to undo YET — both let the button step back instead.
 */
const UNDOABLE_UPLOAD_STATUSES: AddExerciseUploadStatus[] = [
  'waitingForSmartphone',
  'waitingForSmartphoneUpload',
  'uploadFailed',
  'uploadFinished',
];

interface StepRow {
  /** The stepper's headline for this step. */
  title: string;
  /** Where "Weiter" goes, or `undefined` when this step is not left by asking. */
  next: (wizard: AddExerciseWizard) => AddExerciseStep | undefined;
  /** Where "Zurück" goes, or `undefined` when there is no way back. */
  back: (wizard: AddExerciseWizard) => AddExerciseBackTarget;
}

/**
 * The wizard, as a table. Each row is a step; the two edge columns hold the guard order
 * the machine's transition arrays used to hold, top to bottom.
 */
const STEP_TABLE: Record<AddExerciseStep, StepRow> = {
  chooseVisibility: {
    title: 'Bitte wähle',
    next: (wizard) => {
      if (isForSinglePatient(wizard.exercise)) {
        // The phone arrives with the Patient:in already chosen in its URL, so it has
        // nothing to ask and goes straight to the camera.
        return wizard.exercise.sharedWithUserId && wizard.isRemoteSmartphone
          ? 'showUploadScreen'
          : 'choosePatient';
      }
      return skipsInstructions(wizard.exercise) ? 'showUploadScreen' : 'showInstructions';
    },
    back: () => undefined,
  },
  choosePatient: {
    // No headline: the picker below says what is asked.
    title: '',
    next: (wizard) =>
      skipsInstructions(wizard.exercise) ? 'showUploadScreen' : 'showInstructions',
    back: () => ({ step: 'chooseVisibility' }),
  },
  showInstructions: {
    title: 'Anleitung & Hinweise',
    next: () => 'showUploadScreen',
    back: (wizard) =>
      isForSinglePatient(wizard.exercise)
        ? { step: 'choosePatient' }
        : { step: 'chooseVisibility' },
  },
  showUploadScreen: {
    title: 'Übungsvideo hochladen',
    // The phone has to prove who it is before it may describe the exercise; the desktop
    // is already logged in and goes straight on.
    next: (wizard) => (wizard.isRemoteSmartphone ? 'showLoginScreen' : 'showMetadataEditor'),
    back: (wizard) => {
      // Waiting for a phone, holding a video, or holding a rejection: "Zurück" undoes
      // that rather than the step. While a file is actually going up it does NOT — there
      // is nothing to undo yet, so it steps back and the upload is abandoned with the
      // step.
      if (UNDOABLE_UPLOAD_STATUSES.includes(wizard.uploadStatus)) return 'undoUpload';
      if (!skipsInstructions(wizard.exercise)) return { step: 'showInstructions' };
      return isForSinglePatient(wizard.exercise)
        ? { step: 'choosePatient' }
        : { step: 'chooseVisibility' };
    },
  },
  showLoginScreen: {
    title: 'Passwort bestätigen',
    // Left by the keycloak hand-off resolving, not by the user asking.
    next: () => undefined,
    back: () => undefined,
  },
  showMetadataEditor: {
    title: 'Beschreibung hinzufügen',
    next: () => 'showPreview',
    back: () => ({ step: 'showUploadScreen' }),
  },
  showPreview: {
    title: 'Vorschau',
    next: () => 'showFinishedScreen',
    back: () => ({ step: 'showMetadataEditor' }),
  },
  showFinishedScreen: {
    // The stepper does not render on the finished screen, so this title is never shown.
    title: '',
    // The desktop's "Abschließen" closes the panel; the phone has a library to go to.
    next: (wizard) => (wizard.isRemoteSmartphone ? 'redirectToLibrary' : undefined),
    back: () => undefined,
  },
  redirectToLibrary: {
    title: '',
    next: () => undefined,
    back: () => undefined,
  },
};

/** The stepper's headline for a step. */
export function stepTitle(step: AddExerciseStep): string {
  return STEP_TABLE[step].title;
}

/** Where "Weiter" goes from here, or `undefined` when this step is not left by asking. */
export function nextStep(wizard: AddExerciseWizard): AddExerciseStep | undefined {
  return STEP_TABLE[wizard.step].next(wizard);
}

/** What "Zurück" does from here. */
export function backTarget(wizard: AddExerciseWizard): AddExerciseBackTarget {
  return STEP_TABLE[wizard.step].back(wizard);
}

/**
 * Is there a way back from here? Derived from the table — the whole of what used to be a
 * `sidebarStore` subscription reading the machine's currently-accepted events, which made
 * a store observe itself.
 */
export function canGoBack(wizard: AddExerciseWizard): boolean {
  return backTarget(wizard) !== undefined;
}

/**
 * Move to a step, applying what that step clears on arrival.
 *
 * Two of these are load-bearing and both are today's behaviour, kept deliberately:
 * arriving back at step one starts the exercise over, and arriving on the upload step
 * drops whatever video was there — including on the way back from the description form.
 */
export function enterStep(wizard: AddExerciseWizard, step: AddExerciseStep): void {
  wizard.step = step;
  if (step === 'chooseVisibility') {
    wizard.exercise = new CreateExerciseDto();
    wizard.createdExercise = undefined;
  }
  if (step === 'showUploadScreen') {
    clearUploadedVideo(wizard);
  }
}

/** Apply "Zurück". Does nothing where there is no way back. */
export function goBack(wizard: AddExerciseWizard): void {
  const target = backTarget(wizard);
  if (!target) return;
  if (target !== 'undoUpload') {
    enterStep(wizard, target.step);
    return;
  }
  // Abandoning a QR session invalidates its token: the next attempt asks for a new one.
  if (wizard.uploadStatus === 'waitingForSmartphone') wizard.qrCodeToken = undefined;
  clearUploadedVideo(wizard);
}

/**
 * Put an uploaded video on the exercise. The upload's OWN transition is the caller's to
 * make — this device's upload finishing and a phone reporting one ready write the same
 * four fields but move the status differently.
 */
export function applyUploadedVideo(wizard: AddExerciseWizard, video: ExerciseVideoDto): void {
  wizard.exercise.duration = video.duration;
  wizard.exercise.filename = video.filename;
  wizard.exercise.thumbFilename = video.thumbFilename;
  wizard.exercise.originalFilename = video.originalFilename;
}

/** Forget the video, and with it the upload it came from. */
function clearUploadedVideo(wizard: AddExerciseWizard): void {
  wizard.uploadStatus = 'noFile';
  wizard.exercise.duration = 0;
  wizard.exercise.filename = '';
  wizard.exercise.thumbFilename = '';
  wizard.exercise.originalFilename = '';
}

/**
 * The upload's own transitions. Each one names the status it comes from, because the
 * socket can deliver an event at any moment and an unguarded applier would, for
 * instance, report a finished upload with no file behind it.
 */

/** The user asked for the QR code. */
export function requestSmartphoneUpload(wizard: AddExerciseWizard): void {
  if (wizard.uploadStatus !== 'noFile') return;
  wizard.uploadStatus = 'waitingForSmartphone';
}

/** A phone scanned the code and is now on the upload page. */
export function smartphoneConnected(wizard: AddExerciseWizard): void {
  if (wizard.uploadStatus !== 'waitingForSmartphone') return;
  wizard.uploadStatus = 'waitingForSmartphoneUpload';
  wizard.connectedToSmartphone = true;
}

/** The phone finished uploading, and its video is already on this exercise. */
export function smartphoneReady(wizard: AddExerciseWizard): void {
  if (wizard.uploadStatus !== 'waitingForSmartphoneUpload') return;
  wizard.uploadStatus = 'uploadFinished';
}

/** A file was picked on this device. */
export function startUpload(wizard: AddExerciseWizard): void {
  if (wizard.uploadStatus !== 'noFile' && wizard.uploadStatus !== 'uploadFailed') return;
  wizard.uploadStatus = 'uploading';
}

/** This device's upload came back with a video. */
export function finishUpload(wizard: AddExerciseWizard): void {
  if (wizard.uploadStatus !== 'uploading') return;
  wizard.uploadStatus = 'uploadFinished';
}

/** This device's upload was rejected. */
export function failUpload(wizard: AddExerciseWizard): void {
  if (wizard.uploadStatus !== 'uploading') return;
  wizard.uploadStatus = 'uploadFailed';
}

/**
 * "Video entfernen und ersetzen". A phone that is still connected gets to send another
 * one without scanning again.
 */
export function removeUploadedFile(wizard: AddExerciseWizard): void {
  if (wizard.uploadStatus !== 'uploadFinished') return;
  if (wizard.connectedToSmartphone) {
    // The video's own fields are left alone: the phone is expected to send another and
    // overwrite them, and nothing renders them at this status. Clearing them here would
    // be a change, however tidy it looks.
    wizard.uploadStatus = 'waitingForSmartphoneUpload';
    return;
  }
  clearUploadedVideo(wizard);
}

/**
 * The other device saved the exercise. It arrives already created, so this jumps to the
 * finished screen without a save of its own.
 *
 * Only from the upload step, like every applier above and for the same reason: a desktop
 * that showed the QR code and then walked on by itself is mid-form, and the phone's
 * socket must not throw it out of it.
 */
export function finishedOnOtherDevice(wizard: AddExerciseWizard): void {
  if (wizard.step !== 'showUploadScreen') return;
  enterStep(wizard, 'showFinishedScreen');
}
